/**
 * OWNER-DAILY-EOD-REPORT-V2 -- the HERO band: the six ruled business
 * facts, in one dominant KPI band (founder ruling 2026-08-19).
 *
 *   WALK-INS - CONVERTED - NOT CONVERTED - CONVERSION - REVENUE - TICKET
 *
 * NO MOVEMENT CHIPS, NO SPARKLINES (ruling DECISION 2). The current read
 * path carries no prior-period object and no per-day series, so there is
 * nothing truthful to compare against; a placeholder chip, a "stable"
 * badge on uncompared data, or an empty spark shell would each assert a
 * comparison that was never computed. Where comparison truth does not
 * exist, this band RENDERS NOTHING FOR COMPARISON.
 * OWNER-CONSOLE-COMPARISON-PERIOD-V1 supplies the data and turns them on.
 *
 * SAY IT ONCE (ruling amendment 7): Conversion prints the rate alone --
 * its numerator and denominator are already separately visible as
 * Converted and Walk-ins in this same band, so restating "9 of 39" would
 * be the same fact three times.
 *
 * SCOPE-PARAMETERISED: the band knows no role and no clinic set; the
 * caller supplies both the scope and the already-resolved facts.
 */

import { KpiBand } from '@/dashboard/ui';

import { inrMinor, pctLabel, type ReportScope } from './report-scope';

/** The six ruled facts, as the read model already returns them. */
export interface DailyReportKpiFacts {
  readonly walkins: number;
  readonly callCenterSourced: number;
  readonly otherWalkins: number;
  readonly converted: number;
  readonly notConverted: number;
  readonly conversionRatePct: number | null;
  readonly revenueMinorUnits: number;
  readonly ticketSizeMinorUnits: number | null;
}

export type DailyReportKpiKey =
  | 'walkins'
  | 'converted'
  | 'not_converted'
  | 'revenue';

export interface DailyReportKpiBandProps {
  readonly scope: ReportScope;
  readonly facts: DailyReportKpiFacts;
  /** Opens the visits behind a number; omit to render the band inert. */
  readonly onDrill?: (key: DailyReportKpiKey) => void;
  /** The doctor-closed cross-check line -- a compact SECONDARY truth that
   *  never enters the six (money-converted and doctor-closed are
   *  different facts and stay separately labelled). */
  readonly children?: React.ReactNode;
  readonly testId?: string;
}

export default function DailyReportKpiBand({
  scope,
  facts,
  onDrill,
  children,
  testId = 'report-kpi-band',
}: DailyReportKpiBandProps): JSX.Element {
  const drill = (key: DailyReportKpiKey) =>
    onDrill === undefined ? undefined : () => onDrill(key);

  return (
    <KpiBand
      compact
      title={`${scope.periodLabel} \u00B7 ${scope.label}`}
      basis={
        onDrill === undefined
          ? undefined
          : 'Click any number to open the visits behind it.'
      }
      testId={testId}
      cells={[
        {
          key: 'walkins',
          label: 'Walk-ins',
          value: String(facts.walkins),
          meta: `Call-centre ${facts.callCenterSourced} \u00B7 Other ${facts.otherWalkins}`,
          onClick: drill('walkins'),
          testId: 'report-kpi-walkins',
        },
        {
          key: 'converted',
          label: 'Converted',
          value: String(facts.converted),
          onClick: drill('converted'),
          testId: 'report-kpi-converted',
        },
        {
          key: 'not_converted',
          label: 'Not converted',
          value: String(facts.notConverted),
          onClick: drill('not_converted'),
          testId: 'report-kpi-not-converted',
        },
        {
          key: 'conversion',
          label: 'Conversion',
          value: pctLabel(facts.conversionRatePct),
          testId: 'report-kpi-conversion',
        },
        {
          key: 'revenue',
          label: 'Revenue collected',
          value: inrMinor(facts.revenueMinorUnits),
          onClick: drill('revenue'),
          testId: 'report-kpi-revenue',
        },
        {
          // SHIP-LANE-1 OWNER TRUTH CORRECTIONS (FIX 3): THE Owner meaning of
          // "Ticket size", founder-ratified 2026-09-04 --
          // revenue / walk-ins, the banked Partha definition
          // (docs/requirements/partha-dental/OWNER-INTERVIEW-2026-08-02.md
          // lines 43-44: "AVERAGE TICKET SIZE = revenue / walk-ins"). The
          // denominator here is every visit in the day
          // (lib/owner-money-console.ts: revenue / visits.length), which is
          // why the meta says "per walk-in". This name and this formula are
          // the ones that KEEP the string; the MIS-denominator figures on the
          // AGM and COC screens were renamed away from it, not this one.
          key: 'ticket',
          label: 'Ticket size',
          value: inrMinor(facts.ticketSizeMinorUnits),
          meta: 'per walk-in',
          testId: 'report-kpi-ticket',
        },
      ]}
    >
      {children}
    </KpiBand>
  );
}
